import React, { useState } from 'react';
import { Image, FileText, Code, Globe, Trash2, Download, Loader } from 'lucide-react';
import securityApi from '../../../api/securityApi';

const EvidenceItem = ({ evidence, projectId, taskId, onDelete, canDelete = true }) => {
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'screenshot': return <Image className="w-4 h-4 text-purple-500" />;
      case 'document': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'config_snapshot':
      case 'log':
      case 'text': return <Code className="w-4 h-4 text-orange-500" />;
      case 'api_response': return <Globe className="w-4 h-4 text-green-500" />;
      default: return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(evidence.id);
    } catch (err) {
      console.error('Failed to delete evidence:', err);
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  const downloadUrl = evidence.file_url
    ? securityApi.getEvidenceDownloadUrl(projectId, taskId, evidence.id)
    : null;

  const isImage = evidence.type === 'screenshot' ||
    (evidence.file_url && /\.(png|jpg|jpeg)$/i.test(evidence.file_url));
  const isPdf = evidence.file_url && /\.pdf$/i.test(evidence.file_url);
  const isTextEvidence = evidence.content && (evidence.content.text || typeof evidence.content === 'string');

  const textContent = evidence.content?.text ||
    (typeof evidence.content === 'string' ? evidence.content : null);

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <div className="mt-0.5 flex-shrink-0">{getTypeIcon(evidence.type)}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{evidence.title}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                evidence.collection_method === 'automatic'
                  ? 'bg-teal-100 text-teal-700'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {evidence.collection_method === 'automatic' ? 'Auto' : 'Manual'}
              </span>
              <span className="text-xs text-gray-500">
                {evidence.collected_at ? new Date(evidence.collected_at).toLocaleDateString() : ''}
              </span>
              {evidence.file_name && (
                <span className="text-xs text-gray-500 truncate">{evidence.file_name}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {downloadUrl && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-gray-400 hover:text-teal-600 transition-colors"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </a>
          )}
          {canDelete && evidence.collection_method === 'manual' && (
            <>
              {showConfirm ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                  >
                    {deleting ? <Loader className="w-3 h-3 animate-spin" /> : 'Delete'}
                  </button>
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowConfirm(true)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete evidence"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Preview section */}
      {isImage && downloadUrl && (
        <div className="mt-2">
          <img
            src={downloadUrl}
            alt={evidence.title}
            className="max-w-full max-h-48 rounded border border-gray-200 object-contain"
          />
        </div>
      )}

      {isPdf && downloadUrl && (
        <div className="mt-2">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700"
          >
            <FileText className="w-4 h-4" />
            Download PDF
          </a>
        </div>
      )}

      {isTextEvidence && textContent && (
        <div className="mt-2">
          <pre className="text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto max-h-40 overflow-y-auto">
            <code>{textContent}</code>
          </pre>
        </div>
      )}

      {/* Structured content (API responses etc) */}
      {evidence.content && !isTextEvidence && typeof evidence.content === 'object' && !evidence.content.original_filename && (
        <div className="mt-2">
          <pre className="text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto max-h-40 overflow-y-auto">
            <code>{JSON.stringify(evidence.content, null, 2)}</code>
          </pre>
        </div>
      )}
    </div>
  );
};

export default EvidenceItem;
